// ── designStandard.js — 관리자 페이지 디자인 표준 토큰 (2026-08-26 통일) ──────
// 기준 화면: 직원 관리 (사용자 확정). 페이지 통일 시 이 모듈만 import 해서 쓴다.
//   · 흰 배경(AdminLayout 전역) · 텍스트 헤더(제목 26/700 + 부제 12 muted, 데스크톱 상단 = STD_HEADER_TOP)
//   · 버튼 2종(검정 박스=주요 / 흰 외곽선=보조) · 알약 토글(활성=연파랑+파랑 #1d7de6)
//   · 표(흰 카드·라운드 8·1px 테두리·그림자 없음·검정 머리글 띠·마지막 행 밑줄 제거)
//   · 상태 배지(연파랑+파랑) · 검색창(홈 톤, 아래 40px)
// 원본 정의는 usermgmt/styles.js 에서 시작 — 여기로 수렴시킨다 (usermgmt 는 추후 이 모듈로 교체).
import React from 'react';
import { Search } from 'lucide-react';

export const stdPageTitle = {
  margin: 0,
  fontSize: 26,
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: 'var(--text)',
};

export const stdPageSubtitle = {
  marginTop: 4,
  fontSize: 12,
  color: 'var(--text-muted)',
};

// 데스크톱 페이지 머리글 시작 높이 (2026-09-28 사용자: "위에 공간들 다시 올려보자 — 작업대상 위 프로필 박스랑 동일한 선")
//   AdminLayout main 위 여백(12px) + 이 값 = 사이드바 프로필 박스 위치(12px). 전엔 56px(사이드바 '홈' 줄 높이).
//   머리글 높이를 쓰는 곳(표준 헤더 + 직접 적던 7곳)이 전부 이 값을 쓴다 — 바꿀 땐 여기 한 곳만.
export const STD_HEADER_TOP = 0;

// 헤더 행 컨테이너 — 데스크톱 시작 높이 STD_HEADER_TOP
export const stdHeaderRow = (isMobile) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: isMobile ? 'stretch' : 'flex-end',
  gap: 12,
  flexWrap: 'wrap',
  margin: isMobile ? '4px 0 16px' : `${STD_HEADER_TOP}px 0 16px`,
});

// 다크 모드 색은 index.css .dark 의 --std-* 변수(2026-09-28). 라이트에는 변수가 없어 뒤의 종전 값이 쓰인다.
export const stdBlackBtn = {
  border: '1px solid var(--std-btn, #0f172a)',
  borderRadius: 12,
  padding: '8px 14px',
  background: 'var(--std-btn, #0f172a)',
  color: '#fff',
  fontWeight: 700,
  fontSize: 12,
  cursor: 'pointer',
};

export const stdOutlineBtn = {
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: '8px 14px',
  background: 'var(--bg-card)',
  color: 'var(--text-sub)',
  fontWeight: 700,
  fontSize: 12,
  cursor: 'pointer',
};

// 알약 토글 그룹 + 버튼 (활성 = 연파랑 배경 + 파랑 글씨)
export const stdTabGroup = {
  background: 'var(--bg-subtle)',
  borderRadius: 999,
  padding: 3,
  display: 'inline-flex',
};
export const stdTabBtn = (active) => (active
  ? {
      background: 'var(--std-pill-on-bg, rgba(59,154,242,0.12))', borderRadius: 999, padding: '5px 14px',
      fontWeight: 700, fontSize: 12, border: 'none', cursor: 'pointer', color: 'var(--std-pill-on-text, #1d7de6)',
    }
  : {
      background: 'transparent', border: 'none', padding: '5px 14px',
      fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', borderRadius: 999,
    });

// 표 카드 + 셀
export const stdTableWrap = {
  overflowX: 'auto',
  background: 'var(--bg-card)',
  borderRadius: 8,
  overflow: 'hidden',
  border: '1px solid var(--border)',
  boxShadow: 'none',
};
export const stdTable = { width: '100%', borderCollapse: 'collapse' };
export const stdTh = {
  background: 'var(--std-thead, #0f172a)',
  padding: '10px 12px',
  fontSize: 11,
  fontWeight: 600,
  color: 'rgba(255,255,255,0.92)',
  borderBottom: 'none',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};
export const stdTd = {
  padding: '10px 12px',
  fontSize: 13,
  borderBottom: '1px solid var(--bg-subtle)',
  color: 'var(--text)',
  verticalAlign: 'middle',
};
// 마지막 행 셀 — 카드 테두리와 밑줄 겹침(2px 착시) 방지
export const stdTdLast = { ...stdTd, borderBottom: 'none' };
// n행 중 i행의 셀 스타일 선택 헬퍼
export const stdCell = (rowIndex, rowCount) => (rowIndex === rowCount - 1 ? stdTdLast : stdTd);

// 상태 배지 (재직중류 활성 = 연파랑+파랑 / 비활성 회색)
export const stdBadgeActive = {
  display: 'inline-block',
  background: 'var(--std-badge-bg, rgba(59,154,242,0.12))',
  color: 'var(--std-badge-text, #1d7de6)',
  border: 'none',
  borderRadius: 999,
  padding: '3px 11px',
  fontWeight: 700,
  fontSize: 12,
};
export const stdBadgeMuted = {
  display: 'inline-block',
  background: 'var(--border)',
  color: 'var(--text-sub)',
  border: '1px solid var(--border)',
  borderRadius: 999,
  padding: '2px 10px',
  fontWeight: 700,
  fontSize: 12,
};

// 검색창 (홈 '기능 검색' 톤) — 아래 콘텐츠와 40px
export const stdSearchWrap = (isMobile) => ({
  position: 'relative',
  maxWidth: isMobile ? '100%' : 400,
  marginBottom: 40,
});
export const stdSearchInput = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: '10px 12px 10px 34px',
  fontSize: 13,
  color: 'var(--text)',
  background: 'var(--bg-card)',
  outline: 'none',
};
export const stdSearchIcon = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'var(--text-muted)',
  pointerEvents: 'none',
};

// 페이지 검색창 — PC 는 직원 관리 기준(2026-11-04 맵찔찔이님 1a·2a·3b): 폭 최대 400px·좁아지면 줄어듦, 모양도 같게.
//   휴대폰은 페이지마다 지금 폭·모양 그대로(mobileStyle).
export const stdSearchPcBox = {
  position: 'relative',
  width: '100%',
  maxWidth: 400,
  flex: '0 1 400px',
  minWidth: 0,
};
export function StdPageSearch({ isMobile, mobileStyle, ...inputProps }) {
  if (isMobile) return <input {...inputProps} style={mobileStyle} />;
  return (
    <div style={stdSearchPcBox}>
      <Search size={15} style={stdSearchIcon} />
      <input {...inputProps} style={stdSearchInput} />
    </div>
  );
}

// 검색칸 모양의 **선택창**(2026-09-16 사용자 신고: 사파리에서 칸이 작고 아이콘과 이름이 겹침).
//   사파리는 select 를 자기 방식으로 그려서 좌우 여백·높이를 무시한다. 그리기를 끄고 직접 준다.
//   기본 화살표도 사라지므로 호출부가 오른쪽에 stdSearchChevron 아이콘을 얹는다.
export const stdSearchSelect = {
  ...stdSearchInput,
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
  backgroundImage: 'none',
  height: 43,          // 이름 검색 칸과 같은 높이
  lineHeight: '21px',
  paddingRight: 34,    // 화살표 자리
  cursor: 'pointer',
  textOverflow: 'ellipsis',
};

export const stdSearchChevron = {
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'var(--text-muted)',
  pointerEvents: 'none',
};

// ── 모달 헤더 표준 (2026-08-31 사용자 확정) — 계약관리 헤더 구성(아이콘 박스+제목) + 검정 밴드 ──
//   커스텀 모달은 이 토큰을 import, 공유 Modal(components/Modal.jsx)은 같은 값을 내장.
//   라이트 검정 #0f172a = 표 머리글 띠와 같은 색. 다크는 --std-thead(차콜 #2a2a2e, 2026-09-28 — 남색 다크에서 바탕과 구분 안 되던 것).
export const stdModalHeaderBg = 'var(--std-thead, #0f172a)';
export const stdModalHeaderBar = (radius = 16) => ({
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
  padding: '14px 20px', background: stdModalHeaderBg,
  borderRadius: `${radius}px ${radius}px 0 0`, flexShrink: 0,
});
export const stdModalHeaderIconBox = {
  width: 36, height: 36, background: 'rgba(255,255,255,0.18)', borderRadius: 11,
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 17,
};
export const stdModalHeaderTitle = { fontWeight: 800, fontSize: 15, color: '#fff', lineHeight: 1.3 };
export const stdModalHeaderSub = { fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 2 };
export const stdModalCloseBtn = {
  background: 'rgba(255,255,255,0.15)', border: 'none', width: 30, height: 30, borderRadius: 9,
  cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 700,
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
};
